import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import ReactFlow, {
    addEdge,
    Controls,
    Background,
    useNodesState,
    useEdgesState,
    useReactFlow,
    Position,
} from "reactflow";
import "reactflow/dist/style.css";
import { v4 as uuidv4 } from "uuid";
import { useSelector, useDispatch } from "react-redux";
import TriggerRequestNode from "@components/misc/Workflow/TriggerRequestNode";
import TriggerResponseNode from "@components/misc/Workflow/TriggerResponseNode";
import RestApiNode from "@components/misc/Workflow/RestApiNode";
import AddActionNode from "@components/misc/Workflow/AddActionNode";
import { updateWorkflow, updateWorkflowNode, deleteWorkflowNode, updateWorkflowNodePosition, addActionToWorkflow } from "@redux/slices/integrationSlice";
// import { animate } from "framer-motion";


import ParentNode from "@components/misc/Workflow/ParentNode"; // Import the new node
// import { z } from "framer-motion/dist/types.d-B50aGbjN";
// import { a } from "framer-motion/dist/types.d-B50aGbjN";

const nodeTypes = {
    triggerRequest: TriggerRequestNode,
    triggerResponse: TriggerResponseNode,
    restApi: RestApiNode,
    addAction: AddActionNode,
    foreach: ParentNode // Add the new node type
};


const getUniqueId = () => {
    return uuidv4();
}

const WorkflowDiagram = () => {
    const dispatch = useDispatch();
    const currentWorkflow = useSelector((state) => state.integration.currentWorkflow);
    // const [allEdges, setAllEdges] = useState([]);
    const [nodes, setNodes, onNodesChange] = useNodesState([]);
    const [edges, setEdges] = useEdgesState([]);
    const reactFlowWrapper = useRef(null);
    // const triggerNodeRef = useRef(null); // Ref for TriggerNode width
    const [showSidebar, setShowSidebar] = useState(false);
    const [selectedNode, setSelectedNode] = useState(null);

    const { getEdges, getNodes } = useReactFlow(); // Get the getEdges function from useReactFlow


    const getConnectedNodes = useCallback((nodeId) => {
        const connectedNodes = [];
        // console.log(allEdges);
        getEdges().forEach((edge) => {
            if (edge.target === nodeId) {
                //connectedNodes.push(edge.source); // Add source node if it's a target
            } else if (edge.source === nodeId) {
                connectedNodes.push(edge.target); // Add target node if it's a source
            }
        });
        return connectedNodes;
    }, [getEdges]);


    const handleAddActionClick = useCallback((event, nodeId, parentId, nextNodePositionY) => {
        const oldConnectedNodes = getConnectedNodes(nodeId);
        // console.log(parentNodeHeight);

        const newAction = {
            id: uuidv4(),
            data: { label: `Action ${currentWorkflow.actions.length + 1}` },
            type: "restApi",
            parentId: parentId || null, // Set parentId if provided
            position: { x: 200, y: 200 },
            width: 140,
            height: 60,
            invokeAfter: [
                {
                    id: nodeId.replace("add-", ""),
                    status: "success"
                }
            ]
        };

        dispatch(addActionToWorkflow({ newAction, oldConnectedNodes, nextNodePositionY: nextNodePositionY || 60 })); // Default height if not set
    }, [getConnectedNodes, currentWorkflow.actions.length, dispatch]);

    const handleDeleteNode = useCallback((nodeId) => {
        if (nodeId) {
            dispatch(deleteWorkflowNode({ nodeId: nodeId }));
            setSelectedNode(null);
            setShowSidebar(false);
        }
    }, [dispatch]);

    const handleOnNodeClick = useCallback((event, nodeId) => {
        event.stopPropagation(); // Stop event propagation here!
        const selectedNode = getNodes().find(node => node.id === nodeId);
        setSelectedNode(selectedNode);
        setShowSidebar(true);

    }, [getNodes]);

    const handleCloseSidebar = () => {
        setShowSidebar(false);
        setSelectedNode(null);
    };

    const handleBackgroundClick = () => {
        setShowSidebar(false);
        setSelectedNode(null);
    };

    const handleLabelChange = (event) => {
        const newLabel = event.target.value;
        setSelectedNode((prevNode) => ({
            ...prevNode,
            data: {
                ...prevNode.data,
                label: newLabel,
            },
        }));
    };

    const handleApplyButtonClick = () => {
        if (selectedNode) {
            dispatch(updateWorkflowNode({ id: selectedNode.id, label: selectedNode.data.label }));
        }
        handleCloseSidebar();
    };

    const getWidthOfParentNode = (actions, parentId) => {
        const childNodes = actions.filter(action => action.parentId === parentId);
        if (childNodes.length === 0) return null;

        const maxX = childNodes.reduce((max, node) => {
            return Math.max(max, (node.position?.x || 0) + (node.width || 200)); // Default width if not set
        }, 0);
        return maxX + 20; // Add some padding

        // Calculate the maximum width of child nodes
        // const maxWidth = childNodes.reduce((max, node) => {
        //     return Math.max(max, node.width || 200); // Default width if not set
        // }, 0);
        // return maxWidth + 50; // Add some padding
    }

    const getHeightOfParentNode = (actions, parentId) => {
        const childNodes = actions.filter(action => action.parentId === parentId);
        if (childNodes.length === 0) return null;

        // get the node at the bottom most position
        const maxY = childNodes.reduce((max, node) => {
            return Math.max(max, (node.position?.y || 0) + (node.height || 100)); // Default height if not set
        }, 0);
        return maxY;
    };

    const initialNodes = useMemo(() => {
        if (!currentWorkflow?.actions) return [];

        const newNodes = [];

        let startY = 0;

        currentWorkflow.actions.forEach((action) => {
            startY += 60;
            //const triggerNodeWidth = triggerNodeRef.current?.offsetWidth || 120; // Default width if ref is not available


            let nodeSize = {
                width: action.width, // Set width for parent nodes
                height: action.height, // Set height for parent nodes
            };

            if (action.type === "foreach") {
                nodeSize = {
                    width: getWidthOfParentNode(currentWorkflow.actions, action.id) || 200, // Default width if not set
                    height: getHeightOfParentNode(currentWorkflow.actions, action.id) || 100, // Default height if not set
                };
            }

            // console.log(action.parentId);


            // WorkflowDiagram.js
            const actionNode = {
                id: action.id,
                data: {
                    label: action.data.label,
                    id: action.id,
                    parentId: action.parentId || null, // Pass parentId to data for child nodes
                    onClick: handleOnNodeClick,
                    onAddNextActionClick: handleAddActionClick,
                    style: nodeSize,
                    invokeAfter: action.invokeAfter
                },
                type: action.type, // Use "group" type for parent nodes
                position: { x: action.position.x || 0, y: (action.position.y || 60) || startY },
                sourcePosition: Position.Bottom,
                targetPosition: Position.Top,
                draggable: action.draggable,
                parentId: action.parentId || null, // Set parentId if it exists
                extent: action.parentId ? 'parent' : undefined, // Make it a child of the parent node if parentId exists
            };

            newNodes.push(actionNode);
        });

        return newNodes;
    }, [currentWorkflow, handleOnNodeClick, handleAddActionClick]);

    const initialEdges = useMemo(() => {
        if (!currentWorkflow?.actions) return [];

        const newEdges = [];
        currentWorkflow.actions.forEach((action) => {

            if (action.type !== "triggerResponse" && action.invokeAfter && action.invokeAfter.length > 0) {
                action.invokeAfter.forEach((invokeAfter) => {
                    const invokeAfterAction = currentWorkflow.actions.find(a => a.id === invokeAfter.id);
                    if (invokeAfterAction) {
                        newEdges.push({
                            id: `e-add-${getUniqueId()}`,
                            source: `${invokeAfter.id}`,
                            target: action.id,
                            type: "simplebezier",
                            style: {
                                strokeWidth: 1,
                                zIndex: 100,
                                stroke: invokeAfter.status === "success" ? "#000000" : "#ff0000",
                                strokeDasharray: invokeAfter.status === "failed" ? "5 5" : "", // Add this line for dashed lines
                            },
                            sourceHandle: "b",
                            targetHandle: "a",
                        });
                    }
                });
            }
        });
        return [...newEdges, ...(currentWorkflow?.edges || [])];
    }, [currentWorkflow]);

    useEffect(() => {
        setNodes(initialNodes);
    }, [initialNodes, setNodes]);

    useEffect(() => {
        setEdges(initialEdges);
    }, [initialEdges, setEdges]);

    const onConnect = useCallback((params) => setEdges((eds) => addEdge(params, eds)), [setEdges]);

    const onNodesChangeHandler = useCallback((changes) => {
        // console.log(changes)
        onNodesChange(changes);
        changes.forEach(change => {
            if (change.type === 'position' && change.position && change.position.x && change.position.y) {
                dispatch(updateWorkflowNodePosition({ id: change.id, position: change.position }));
            }
        });
    }, [dispatch, onNodesChange]);

    const onEdgesChangeHandler = useCallback((changes) => {
        const newEdges = changes.edges;
        dispatch(updateWorkflow({ edges: newEdges }));
    }, [dispatch]);

    const onReactFlowInit = useCallback((reactFlowInstance) => {
        setTimeout(() => {
            reactFlowInstance.fitView();
        }, 100);
    }, []);


    return (
        <div className="relative h-[calc(100vh-190px)] border" ref={reactFlowWrapper}>
            {showSidebar && (
                <div className="absolute right-0 top-0 z-10 h-full w-[33%] overflow-auto rounded-md border border-blue-400 bg-white p-4">
                    {selectedNode && (
                        <div>
                            <h2 className="text-lg font-semibold">Node Details</h2>
                            <p>ID: {selectedNode.id}</p>
                            <p>Type: {selectedNode.type}</p>
                            <label htmlFor="node-label">Label:</label>
                            <input
                                type="text"
                                maxLength={100}
                                id="node-label"
                                value={selectedNode.data.label}
                                onChange={handleLabelChange}
                                className="mt-1 h-10 w-full rounded border bg-gray-50 px-4"
                            />
                            {/* Add more details as needed */}
                        </div>
                    )}
                    <div className="absolute bottom-0 right-4 flex gap-2">
                        <button
                            onClick={handleCloseSidebar}
                            className="mb-4 rounded-md bg-gray-500 p-2 text-white hover:bg-gray-600"
                        >
                            Close
                        </button>
                        <button
                            onClick={() => handleDeleteNode(selectedNode.id)}
                            className="mb-4 rounded-md bg-gray-500 p-2 text-white hover:bg-gray-600"
                        >
                            Delete
                        </button>
                        <button
                            onClick={handleApplyButtonClick}
                            className="mb-4 rounded-md bg-blue-500 p-2 text-white hover:bg-blue-600"
                        >
                            Apply
                        </button>
                    </div>
                </div>
            )}
            <ReactFlow
                nodes={nodes}
                edges={edges}
                onNodesChange={onNodesChangeHandler}
                onEdgesChange={onEdgesChangeHandler}
                nodeTypes={nodeTypes}
                onConnect={onConnect}
                defaultEdgeOptions={{
                    type: "simplebezier",
                    style: { strokeWidth: 1, stroke: "#000" },
                }}
                fitViewOptions={{
                    padding: 0.1,
                    duration: 500,
                    maxZoom: 2,
                    minZoom: 0.5,
                }}
                onInit={onReactFlowInit}
                onClick={handleBackgroundClick}
            >
                <Controls />
                <Background gap={10} />
            </ReactFlow>
        </div>
    );
};

export default WorkflowDiagram;
